import { Button, Typography, Flex, message, Image, Tag } from 'antd';
import { CheckIcon, XIcon } from '@phosphor-icons/react';
import styled from '@emotion/styled';
import { motion } from 'motion/react';
import { updateAttributesOfCdnImage } from '@admin-panel/utils';
import { WorkflowItemDTOType } from '../../../../schemas';
import {
  useAcceptWorkflowItemMutation,
  useRejectWorkflowItemMutation,
} from '../../../../store/workflows/workflowApi';

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
`;

export const ImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 2/3;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;

  .ant-image {
    height: 100%;
  }
`;

const TagsOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 100%);
  padding: 8px 6px 6px 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  align-items: flex-end;
`;

const SmallTag = styled(Tag)`
  font-size: 8px;
  line-height: 1.2;
  padding: 1px 3px;
  margin: 0;
  border-radius: 3px;
  height: auto;
  min-height: 14px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.8);
  color: #1d39c4;
  backdrop-filter: blur(4px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

interface IWorkflowItemVerticalProps {
  data: WorkflowItemDTOType;
  lastItemRef?: (node: HTMLDivElement | null) => void;
}

const WorkflowItemVertical = ({ data, lastItemRef }: IWorkflowItemVerticalProps) => {
  const [acceptItem, { isLoading: isAccepting }] = useAcceptWorkflowItemMutation();
  const [rejectItem, { isLoading: isRejecting }] = useRejectWorkflowItemMutation();

  const handleAccept = async () => {
    try {
      await acceptItem({
        workflowId: data.workflowId,
        runId: data.workflowRunId,
        itemId: data.id,
        acceptedTags: data.proposedTags ?? [],
      }).unwrap();

      message.success('Item accepted');
    } catch {
      message.error('Failed to accept item');
    }
  };

  const handleReject = async () => {
    try {
      await rejectItem({
        workflowId: data.workflowId,
        runId: data.workflowRunId,
        itemId: data.id,
        rejectedTags: data.proposedTags ?? [],
      }).unwrap();

      message.success('Item rejected');
    } catch {
      message.error('Failed to reject item');
    }
  };

  return (
    <Wrapper
      ref={lastItemRef}
      initial={{ opacity: 1, scale: 1 }}
      exit={{
        opacity: 0,
        scale: 0.8,
        y: -20,
        transition: {
          duration: 0.2,
          ease: [0.25, 0.46, 0.45, 0.94],
        },
      }}
      whileHover={{
        scale: 1.02,
        transition: { duration: 0.2 },
      }}
      whileTap={{
        scale: 0.98,
        transition: { duration: 0.1 },
      }}
    >
      <ImageWrapper>
        {data.images && data.images.length > 0 ? (
          <Image
            src={updateAttributesOfCdnImage(data.images[0].url, { width: 320 })}
            alt="Product"
            style={{
              objectFit: 'contain',
              width: '100%',
              height: '100%',
              borderRadius: 8,
              background: '#fafafa',
              display: 'block',
            }}
            preview={false}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#bbb',
              fontSize: 14,
              border: '1px solid #f4f4f4',
              borderRadius: 8,
            }}
          >
            No Image
          </div>
        )}

        {data.proposedTags && data.proposedTags.length > 0 && (
          <TagsOverlay>
            {data.proposedTags.map((tag, index) => (
              <SmallTag key={`${tag}-${index}`}>{tag}</SmallTag>
            ))}
          </TagsOverlay>
        )}
      </ImageWrapper>
      <Typography.Text
        style={{
          textTransform: 'uppercase',
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        {data.brandName}
      </Typography.Text>

      <Typography.Text
        style={{
          fontWeight: 400,
          marginBottom: 4,
        }}
      >
        {data.name}
      </Typography.Text>

      <Typography.Text
        style={{
          fontWeight: 600,
          fontSize: 12,
        }}
      >
        ₹{data.mrp}
      </Typography.Text>

      <Flex
        gap={12}
        justify="space-between"
        style={{
          paddingTop: 12,
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Button
          onClick={handleAccept}
          loading={isAccepting}
          variant="outlined"
          color="blue"
          icon={<CheckIcon />}
          style={{
            borderRadius: 10,
            minWidth: 0,
            maxWidth: '48%',
            flex: '1 1 0',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            fontSize: 12,
            gap: 2,
          }}
        >
          Accept
        </Button>
        <Button
          color="red"
          variant="outlined"
          icon={<XIcon />}
          onClick={handleReject}
          loading={isRejecting}
          style={{
            borderRadius: 10,
            minWidth: 0,
            maxWidth: '48%',
            flex: '1 1 0',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            gap: 2,
            fontSize: 12,
          }}
        >
          Reject
        </Button>
      </Flex>
    </Wrapper>
  );
};

export default WorkflowItemVertical;
